import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fileUrl } from '@/lib/api';
import { useMotion, useReveal } from '@/lib/motion';
import SectionBackground from '@/components/SectionBackground';
import Lightbox from '@/components/Lightbox';
import PresentingSponsorBadge from '@/components/PresentingSponsorBadge';

// Fallback for the Sponsorship Opportunities CTA when the admin URL is blank.
const SPONSORSHIP_MAILTO_FALLBACK = 'mailto:tiffanys@sei-con.org?subject=Sponsorship%20Opportunities%20Inquiry';

export default function PartnersGallerySection({ partners = [], gallery = [], section = {}, meta = {}, motion = {}, sponsorshipUrl, presentingSponsor }) {
  const navigate = useNavigate();
  const headerLeft = useReveal();
  const headerRight = useReveal();
  const partnersGrid = useReveal();
  const galleryGrid = useReveal();
  const { isMobile } = useMotion();
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const marqueeEnabled = motion?.wow?.marquee_partners !== false && motion?.wow?.marquee_mode !== 'grid';
  const marqueeSpeed = motion?.wow?.marquee_speed || 'normal';
  // (reveals.partners / reveals.gallery toggles control the reveal animation in motion lib;
  // here we just consume meta + section appearance defaults.)

  const partnersEyebrow = meta.partners_eyebrow || 'POWERED BY PARTNERS.';
  const partnersHeadline = meta.partners_headline || 'DRIVEN BY LEADERS.';
  const partnersButtonLabel = meta.partners_button_label || 'View All Partners';
  const partnersButtonUrl = meta.partners_button_url || '/partners';
  const sponsorshipHref = (sponsorshipUrl && sponsorshipUrl.trim()) ? sponsorshipUrl.trim() : SPONSORSHIP_MAILTO_FALLBACK;
  const galleryEyebrow = meta.gallery_eyebrow || 'PAST EXPERIENCES.';
  const galleryHeadline = meta.gallery_headline || 'BUILT FOR WHAT’S NEXT.';
  const galleryButtonLabel = meta.gallery_button_label || 'View Gallery';
  const galleryButtonUrl = meta.gallery_button_url || '/gallery';

  // Round 3 — Homepage Gallery picker. When configured, tiles come
  // from the server-side-hydrated selection (bypassing the /public/home
  // 100-cap and the front-of-order rule). Server injects the array
  // into meta.homepage_gallery_hydrated as an ephemeral response-time
  // field; it is NEVER stored on the DB's sections_meta.sponsors doc.
  //
  // Fallback rules (verbatim from the spec):
  //   - UNCONFIGURED (never saved): render exactly today's behavior —
  //     first six visible gallery photos sorted by `order`.
  //   - CONFIGURED with all six valid: render the six in configured
  //     order.
  //   - CONFIGURED but some IDs invalid (hidden or deleted after save):
  //     render the remaining valid ones in configured order, no
  //     arbitrary substitution. If NONE remain valid, still do NOT
  //     silently fall back to arbitrary photos — render zero tiles and
  //     let the admin fix the selection.
  //
  // R2 Lightbox restore: the lightbox source is a de-duplicated
  // UNION — the existing `gallery` array first in its original order,
  // then any configured off-cap photos not already present appended
  // afterward. Dedup key is the photo `id`. Rationale: preserve the
  // full past-experiences catalog for browsing (regression fix from
  // the prior scope that restricted the lightbox to just the curated
  // six), while still making any off-cap configured photo reachable
  // when the user opens the lightbox from a homepage tile.
  const hgHydrated = (meta && meta.homepage_gallery_hydrated) || null;
  const isHomepageConfigured = !!(hgHydrated && hgHydrated.configured);
  const configuredPhotos = Array.isArray(hgHydrated && hgHydrated.photos) ? hgHydrated.photos : [];
  const homepageTiles = isHomepageConfigured ? configuredPhotos : gallery.slice(0, 6);
  const lightboxPhotos = (() => {
    const seen = new Set();
    const out = [];
    // Prefer gallery order; skip malformed entries missing an id.
    (gallery || []).forEach((g) => {
      if (!g) return;
      const gid = g.id;
      if (!gid || seen.has(gid)) return;
      seen.add(gid);
      out.push(g);
    });
    // Append any configured photos not already present, in configured order.
    configuredPhotos.forEach((g) => {
      if (!g) return;
      const gid = g.id;
      if (!gid || seen.has(gid)) return;
      seen.add(gid);
      out.push(g);
    });
    return out;
  })();

  const speedSec = marqueeSpeed === 'slow' ? 90 : marqueeSpeed === 'fast' ? 35 : 60;
  const duration = isMobile ? speedSec * 1.5 : speedSec;

  const goTo = (url) => () => {
    if (!url) return;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (url.startsWith('#')) {
      const el = document.querySelector(url);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Synchronously persist the current scroll position so the POP-restore
      // handler in ScrollToTop can land the user back here on browser-back.
      try { sessionStorage.setItem('scrollPos:' + window.location.pathname, String(window.scrollY)); } catch (_) { /* ignore */ }
      navigate(url);
    }
  };

  return (
    <>
      {/* Partners section — white background. v3.8: split from the formerly
          shared cream wrapper that paired this block with the gallery in a
          desktop two-column grid. Now full-width, single-column at every
          breakpoint. Background is hardcoded white in this component (does
          not consume the `section` admin prop) so the partners band reads
          as an intentional, distinct surface above the cream gallery band. */}
      <SectionBackground fallbackColor="#FFFFFF">
        <section id="sponsors" className="section-pad relative" data-testid="section-sponsors">
          <div className="container-x relative z-10">
            <div data-testid="partners-block">
              <div ref={headerLeft.ref} className={`${headerLeft.simple ? 'reveal-simple' : 'reveal-up'} ${headerLeft.revealed ? 'is-in' : ''}`}>
                {/* v3.15 — Eyebrow size from sections_meta.sponsors.partners_eyebrow_size.
                    Defensive validation: integer in [16, 48]. Independent
                    of gallery_eyebrow_size (the Gallery eyebrow below). */}
                {(() => {
                  const _raw = meta.partners_eyebrow_size;
                  const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
                  return (
                    <p className="eyebrow eyebrow-lg" data-testid="partners-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{partnersEyebrow}</p>
                  );
                })()}
                <h2 className="mt-3 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[36px] lg:text-[44px]" data-testid="partners-headline">
                  {partnersHeadline}
                </h2>
              </div>

              {presentingSponsor && presentingSponsor.enabled && presentingSponsor.show_in_partners !== false && (
                <div
                  className="mt-6 p-5 md:p-6 bg-white border border-border-line rounded-sm shadow-sm flex flex-col items-center text-center"
                  data-testid="partners-presenting-sponsor-card"
                >
                  <PresentingSponsorBadge
                    sponsor={presentingSponsor}
                    eyebrow={presentingSponsor.partners_eyebrow_label}
                    theme="light"
                    size="lg"
                    testid="partners-presenting-sponsor"
                    align="center"
                    showName
                  />
                </div>
              )}

              {marqueeEnabled ? (
                <div ref={partnersGrid.ref} className={`mt-8 marquee-wrap overflow-hidden ${partnersGrid.simple ? 'reveal-simple' : 'reveal-up'} ${partnersGrid.revealed ? 'is-in' : ''}`} style={{ '--marquee-duration': `${duration}s` }}>
                  <div className="marquee-track flex gap-2 w-max">
                    {[...partners, ...partners].map((p, i) => (
                      <PartnerCell key={`${p.id || i}-${i}`} p={p} />
                    ))}
                  </div>
                </div>
              ) : (
                <div ref={partnersGrid.ref} className={`mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 ${partnersGrid.simple ? 'reveal-simple' : 'reveal-up'} ${partnersGrid.revealed ? 'is-in' : ''}`}>
                  {partners.slice(0, 8).map((p, i) => (
                    <PartnerCell key={p.id || i} p={p} />
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <button className="btn-secondary" onClick={goTo(partnersButtonUrl)} data-testid="view-all-partners">{partnersButtonLabel}</button>
                <a
                  href={sponsorshipHref}
                  target={sponsorshipHref.startsWith('http') ? '_blank' : undefined}
                  rel={sponsorshipHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="btn-secondary"
                  data-testid="partners-sponsorship"
                >Sponsorship Opportunities</a>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* Gallery section — cream background (unchanged hue: #F4EFE7).
          v3.8: previously shared the partners' wrapper; now its own band
          below the white partners section. The new id="gallery" provides
          a clean scroll anchor for any future sticky-nav link to this
          section without changing existing nav behavior. */}
      <SectionBackground fallbackColor="#F4EFE7">
        <section id="gallery" className="section-pad relative" data-testid="section-gallery">
          <div className="container-x relative z-10">
            <div data-testid="gallery-block">
              <div ref={headerRight.ref} className={`${headerRight.simple ? 'reveal-simple' : 'reveal-up'} ${headerRight.revealed ? 'is-in' : ''}`}>
                {/* v3.15 — Eyebrow size from sections_meta.sponsors.gallery_eyebrow_size.
                    Defensive validation: integer in [16, 48]. Independent
                    of partners_eyebrow_size (the Partners eyebrow above). */}
                {(() => {
                  const _raw = meta.gallery_eyebrow_size;
                  const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
                  return (
                    <p className="eyebrow eyebrow-lg" data-testid="gallery-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{galleryEyebrow}</p>
                  );
                })()}
                <h2 className="mt-3 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[36px] lg:text-[44px]" data-testid="gallery-headline">
                  {galleryHeadline}
                </h2>
              </div>
              <div ref={galleryGrid.ref} className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
                {homepageTiles.map((g, i) => (
                  <button
                    type="button"
                    key={g.id || i}
                    onClick={() => {
                      // Map the clicked tile to its position inside the
                      // union lightbox source so the correct photo opens.
                      // Fallback to the tile index only when the tile
                      // has no id (defensive; should not happen in practice).
                      const targetId = g && g.id;
                      let idx = -1;
                      if (targetId) {
                        idx = lightboxPhotos.findIndex((p) => p && p.id === targetId);
                      }
                      setLbIndex(idx >= 0 ? idx : i);
                      setLbOpen(true);
                    }}
                    className={`relative overflow-hidden rounded-sm bg-card-dark cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${galleryGrid.simple ? 'reveal-simple' : 'reveal-card'} ${galleryGrid.revealed ? 'is-in' : ''}`}
                    style={{ aspectRatio: '4/3', transitionDelay: galleryGrid.revealed ? `${i * 80}ms` : '0ms' }}
                    aria-label={g.caption || `Open photo ${i + 1}`}
                    data-testid="gallery-tile-home"
                  >
                    <img src={fileUrl(g.image)} alt={g.caption || 'Past experience'} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  </button>
                ))}
              </div>
              <div className="mt-8">
                <button className="btn-secondary" onClick={goTo(galleryButtonUrl)} data-testid="view-gallery">{galleryButtonLabel}</button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {lightboxPhotos.length > 0 && (
        <Lightbox
          photos={lightboxPhotos}
          initialIndex={lbIndex}
          isOpen={lbOpen}
          onClose={() => setLbOpen(false)}
        />
      )}
    </>
  );
}

function PartnerCell({ p }) {
  const hasLogo = !!p.logo;
  return (
    <a
      href={p.url || '#'}
      target={p.url && p.url !== '#' ? '_blank' : undefined}
      rel="noopener noreferrer"
      className="group relative h-16 w-28 shrink-0 border border-border-line flex items-center justify-center px-2 bg-bg-elevated"
      data-testid="partner-cell"
    >
      {hasLogo ? (
        <img src={fileUrl(p.logo)} alt={p.name} className="max-h-12 max-w-full object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition" />
      ) : (
        <span className="font-display font-semibold uppercase text-ink-soft group-hover:text-ink-primary text-[12px] text-center tracking-wider2 leading-tight transition">{p.name}</span>
      )}
    </a>
  );
}
